import type { LngLat, Route, RouteStep } from '../shared/types';
import { bearing, cumulativeDistances, distance, projectToSegment } from './geo';
import { junctionKey, laneGuide, shortToward, type Junction, type LaneGuide } from './lanes';

/**
 * 高速道路モードのロジック（描画に依存しない）。
 * ルートの高速区間と、OSM から作った施設データ（public/data/highway-facilities.json）を照合して、
 * このルートで通る IC・JCT・SA・PA・料金所を、ルート始点からの距離つきで並べる。
 */

export type FacilityKind = 'IC' | 'JCT' | 'SA' | 'PA' | 'TOLL';
/** j = 本線上の分岐点（ルートの線に正確に乗る）、a = SA・PA の敷地の中心、t = 料金所 */
export type FacilitySource = 'j' | 'a' | 't';

export interface Facility {
  kind: FacilityKind;
  name: string;
  position: LngLat;
  src: FacilitySource;
}

/** ルート上の施設 */
export interface RouteFacility {
  kind: FacilityKind;
  /** 表示名（「(上り)」などの向きは取り除く） */
  name: string;
  /** ルート始点からの距離 [m] */
  along: number;
}

export interface HighwaySection {
  /** ルート始点からの距離 [m] */
  start: number;
  end: number;
  /** 降りる IC（区間の終わりにある出口）。分からなければ undefined */
  exit?: string;
}

export interface HighwayPlan {
  sections: HighwaySection[];
  /** 始点からの距離の順 */
  facilities: RouteFacility[];
  /**
   * 読み上げない案内のインデックス。高速上で IC・JCT・出口のどれにも当たらない「車線を選ぶだけ」の地点
   * （料金所の先の分かれ目など）。案内バーには出す
   */
  quietSteps: Set<number>;
  /** 案内地点（steps のインデックス）ごとの車線案内と行き先 */
  guides: Map<number, LaneGuide>;
}

/** 施設データ（JSON）の1件: [種類, 名前, 経度, 緯度, 取り方] */
export type FacilityRow = [FacilityKind, string, number, number, FacilitySource];

export function parseFacilities(rows: FacilityRow[]): Facility[] {
  return rows.map(([kind, name, lng, lat, src]) => ({ kind, name, position: [lng, lat], src }));
}

/**
 * ルートの線からこの距離以内なら「このルートで通る」とみなす [m]。
 * 本線上の分岐点はルートの座標と同じ OSM の点なのでほぼ 0m。反対車線の分岐点は 10〜30m 離れているので、小さくして向きを取り違えない
 */
const MATCH_RADIUS: Record<FacilitySource, number> = { j: 3, t: 30, a: 80 };
/** 区間に入った直後の IC・料金所（乗ってきた入口）は出さない [m] */
const ENTRY_SKIP = 1000;

/**
 * 表示名にそろえる。
 * 「海老名SA(下り)」→「海老名SA」、「駿河湾沼津SA(下り)、スマートIC」→「駿河湾沼津SA」
 */
export function displayName(name: string, kind: FacilityKind): string {
  let s = name.replace(/\s*[(（][^)）]*[)）]\s*/g, '').trim();
  // SA・PA は併設のスマート IC などを後ろに書いていることが多いので、最初の部分だけにする
  if (kind === 'SA' || kind === 'PA') s = s.split(/[、;／/]/)[0].trim();
  return s || name;
}

/** IC・SIC など「〜で降ります」と言える名前か（首都高の出口は「東扇島」のように接尾辞が無い） */
const hasExitSuffix = (name: string) => /IC|インター|JCT|ジャンクション/.test(name);

/** junctions: 分岐点の車線データ（無ければ車線案内なし） */
export function buildHighwayPlan(route: Route, all: Facility[], junctions?: Map<string, Junction>): HighwayPlan {
  const ranges = route.highways ?? [];
  if (!ranges.length) return { sections: [], facilities: [], quietSteps: new Set(), guides: new Map() };
  const line = route.geometry;
  const cum = cumulativeDistances(line);

  // 高速区間の周りだけに絞る（全国 8,000 件を毎回ルート全体と比べない）
  const pad = 0.002; // 約200m
  const boxes = ranges.map(([from, to]) => {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (let i = from; i <= to && i < line.length; i++) {
      const [x, y] = line[i];
      minX = Math.min(minX, x); maxX = Math.max(maxX, x);
      minY = Math.min(minY, y); maxY = Math.max(maxY, y);
    }
    return { from, to, minX: minX - pad, minY: minY - pad, maxX: maxX + pad, maxY: maxY + pad };
  });

  type Hit = RouteFacility & { src: FacilitySource; offset: number; base: string };
  const hits: Hit[] = [];
  for (const f of all) {
    const [x, y] = f.position;
    for (const b of boxes) {
      if (x < b.minX || x > b.maxX || y < b.minY || y > b.maxY) continue;
      let best: { offset: number; along: number } | null = null;
      for (let i = b.from; i < b.to && i < line.length - 1; i++) {
        const { point, t } = projectToSegment(f.position, line[i], line[i + 1]);
        const offset = distance(f.position, point);
        if (!best || offset < best.offset) best = { offset, along: cum[i] + (cum[i + 1] - cum[i]) * t };
      }
      if (best && best.offset <= MATCH_RADIUS[f.src]) {
        const name = displayName(f.name, f.kind);
        hits.push({ kind: f.kind, name, base: name, along: best.along, src: f.src, offset: best.offset });
      }
    }
  }

  // 同じ名前が近くに複数あるとき（分岐点と敷地、上り線と下り線の敷地など）は1つにする。
  // 本線上の分岐点（j）を優先し、次にルートに近いもの
  const rank = { j: 0, t: 1, a: 2 } as const;
  hits.sort((p, q) => rank[p.src] - rank[q.src] || p.offset - q.offset);
  const kept: Hit[] = [];
  for (const h of hits) {
    if (kept.some((k) => k.base === h.base && Math.abs(k.along - h.along) < 1500)) continue;
    kept.push(h);
  }

  const sections: HighwaySection[] = ranges.map(([from, to]) => ({ start: cum[from], end: cum[Math.min(to, cum.length - 1)] }));
  const facilities = kept
    .filter((h) => !sections.some((s) => (h.kind === 'IC' || h.kind === 'TOLL') && h.along >= s.start && h.along < s.start + ENTRY_SKIP))
    .sort((p, q) => p.along - q.along)
    .map(({ kind, name, along }) => ({ kind, name, along }));

  // 降りる IC: 区間の終わり付近（1.5km 手前〜300m 先）にある最後の IC・JCT
  for (const s of sections) {
    const exit = facilities.filter((f) => (f.kind === 'IC' || f.kind === 'JCT') && f.along >= s.end - 1500 && f.along <= s.end + 300).pop();
    s.exit = exit?.name;
  }
  return { sections, facilities, quietSteps: new Set(), guides: junctions ? laneGuides(route, ranges, cum, junctions) : new Map() };
}

/**
 * 高速区間の中でルートが通る分岐点を探し、その地点の案内（steps）に車線案内を付ける。
 * 分岐点はルートの座標と同じ OSM の点なので、座標のキーで一致を見る
 */
function laneGuides(route: Route, ranges: [number, number][], cum: number[], junctions: Map<string, Junction>): Map<number, LaneGuide> {
  const line = route.geometry;
  const stepAlong = route.steps.map((s) => cum[Math.min(s.startIndex, cum.length - 1)]);
  const out = new Map<number, LaneGuide>();
  for (const [from, to] of ranges) {
    for (let i = Math.max(1, from); i <= to && i < line.length - 1; i++) {
      const j = junctions.get(junctionKey(line[i]));
      if (!j) continue;
      // 分岐のあと 40m ほど先までの向き（分岐直後の細かい曲がりに引きずられないように）
      let k = i + 1;
      while (k < line.length - 1 && cum[k] - cum[i] < 40) k++;
      const guide = laneGuide(j, bearing(line[i], line[k]));
      if (!guide) continue;
      // この分岐で案内している地点（ORS は分岐点で案内を区切る）。案内が無ければ道なりなので付けない
      const s = stepAlong.findIndex((a) => Math.abs(a - cum[i]) <= 60);
      if (s >= 0 && !out.has(s)) out.set(s, guide);
    }
  }
  return out;
}

/** その地点が高速区間の中か（入口の少し手前から高速として扱う） */
export function sectionAt(plan: HighwayPlan, along: number, lead = 300): HighwaySection | undefined {
  return plan.sections.find((s) => along >= s.start - lead && along <= s.end);
}

const DIRECTION: Partial<Record<RouteStep['maneuver'], string>> = {
  'slight-left': '左方向',
  left: '左方向',
  'sharp-left': '左方向',
  'slight-right': '右方向',
  right: '右方向',
  'sharp-right': '右方向',
  straight: '直進',
};

/**
 * 高速区間の中の案内文に、IC・JCT の名前を入れる（plan.quietSteps も埋める）。
 * ORS の高速上の案内は「右方向です」だけで名前が無いので、分岐点（案内地点の前後 150m）の施設名で補う。
 *   - 区間の出口 → 「綾瀬SICで左方向、出口です」（首都高の出口は「東扇島出口で左方向です」）
 *   - それ以外   → 「海老名JCTを右方向です」
 */
export function enrichSteps(route: Route, plan: HighwayPlan): RouteStep[] {
  if (!plan.sections.length) return route.steps;
  const cum = cumulativeDistances(route.geometry);
  return route.steps.map((step, i) => {
    const along = cum[Math.min(step.startIndex, cum.length - 1)];
    // 区間の中だけ。入口の案内（「東名高速道路へ右方向です」）は区間の手前、
    // 区間の終わりの地点は高速を降りたあとの一般道の案内なので、どちらも対象外
    const section = plan.sections.find((s) => along >= s.start + 50 && along < s.end - 1);
    const dir = DIRECTION[step.maneuver];
    if (!section || !dir) return step;
    const near = plan.facilities.find((f) => (f.kind === 'IC' || f.kind === 'JCT') && Math.abs(f.along - along) <= 150);
    const guide = plan.guides.get(i);
    const toward = guide?.toward ? `、${shortToward(guide.toward)}方面` : '';
    // 案内文に行き先があれば（Valhalla は標識の行き先を入れる）車線を選ぶだけの地点でも読み上げる
    if (step.maneuver === 'straight' || (!near && !toward && !step.instruction.includes('方面'))) {
      plan.quietSteps.add(i);
      return step;
    }
    // 名前の無い分岐でも、行き先が分かれば「左方向、横浜・町田方面です」と案内する
    if (!near) return toward ? { ...step, instruction: `${dir}${toward}です` } : step;
    const isExit = near.name === section.exit;
    const instruction = !isExit
      ? `${near.name}を${dir}${toward}です`
      : hasExitSuffix(near.name)
        ? `${near.name}で${dir}、出口です`
        : `${near.name}出口で${dir}です`;
    return { ...step, instruction };
  });
}
